import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Pagination, STATUS_TONE, fmtDate, daysUntil, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import ContractForm, { CONTRACT_STATUSES, blankContract } from '../components/forms/ContractForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const statusTemplate = (row) => <Badge tone={STATUS_TONE[row.status]}>{label(row.status)}</Badge>;

const valueTemplate = (row) => (row.value_eur ? `€${Number(row.value_eur).toLocaleString()}` : '—');

const expiryTemplate = (row) => {
  const d = daysUntil(row.expiry_date);
  const soon = d !== null && d < 30 && ['sent', 'signed'].includes(row.status);
  return <span className={soon ? 'soon' : ''}>{fmtDate(row.expiry_date)}</span>;
};

export default function Contracts() {
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [list, setList] = useState(null);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/contracts?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => {
    cachedFetch('/projects', () => api.get('/projects'))
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]));
  }, []);

  const columns = [
    { field: 'title', headerText: t('Title'), width: 200 },
    { field: 'project_name', headerText: t('Project'), width: 160 },
    { field: 'type', headerText: t('Type'), width: 120 },
    { headerText: t('Status'), width: 110, template: statusTemplate },
    { headerText: t('Value'), width: 110, template: valueTemplate },
    { headerText: t('Expires'), width: 120, template: expiryTemplate },
  ];

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Contracts')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename="contracts"
            rows={list || []}
            columns={[
              { header: t('Title'), field: 'title' },
              { header: t('Project'), field: 'project_name' },
              { header: t('Type'), field: 'type' },
              { header: t('Status'), field: 'status' },
              { header: t('Value'), value: (r) => r.value_eur ?? '' },
              { header: t('Expires'), field: 'expiry_date' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => setEditing({ ...blankContract })}>
              <IconPlus width={15} height={15} /> {t('New contract')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('All statuses')}</option>
          {CONTRACT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No contracts yet.')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={canEdit ? setEditing : undefined}
          onRowAction={canEdit ? setEditing : null}
          actionLabel={t('Open')}
        />
      )}

      <Pagination meta={meta} onPage={setPage} />

      {editing && (
        <ContractForm initial={editing} projects={projects} isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)} onSaved={() => { invalidate('/contracts'); setEditing(null); load(); }} />
      )}
    </div>
  );
}